import React, { useState } from "react";
import { Quill } from "react-quill";
import katex from "katex";
import { Button, Spinner, Modal, Form } from "react-bootstrap";
import ChallengeBlot from "./ChallengeBlot";
import { faLightbulb } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next"; // Importer useTranslation

// Custom Undo button icon component for Quill editor. You can import it directly
// from 'quill/assets/icons/undo.svg' but I found that a number of loaders do not
// handle them correctly
const CustomUndo = () => (
  <svg viewBox="0 0 18 18">
    <polygon className="ql-fill ql-stroke" points="6 10 4 12 2 10 6 10" />
    <path
      className="ql-stroke"
      d="M8.09,13.91A4.6,4.6,0,0,0,9,14,5,5,0,1,0,4,9"
    />
  </svg>
);

// Redo button icon component for Quill editor
const CustomRedo = () => (
  <svg viewBox="0 0 18 18">
    <polygon className="ql-fill ql-stroke" points="12 10 14 12 16 10 12 10" />
    <path
      className="ql-stroke"
      d="M9.91,13.91A4.6,4.6,0,0,1,9,14a5,5,0,1,1,5-5"
    />
  </svg>
);

// Undo and redo functions for Custom Toolbar
function undoChange() {
  this.quill.history.undo();
}
function redoChange() {
  this.quill.history.redo();
}

// Add sizes to whitelist and register them
const Size = Quill.import("formats/size");
Size.whitelist = ["extra-small", "small", "medium", "large"];
Quill.register(Size, true);

// Add fonts to whitelist and register them
const Font = Quill.import("formats/font");
Font.whitelist = [
  "arial",
  "comic-sans",
  "courier-new",
  "georgia",
  "helvetica",
  "lucida",
];
Quill.register(Font, true);

// Enregistrer le format Challenge
if (!Quill.imports["formats/challenge"]) {
  Quill.register(ChallengeBlot);
}

// Fonction pour insérer un challenge
function insertChallenge() {
  const quill = this.quill;
  if (!quill) return;

  const range = quill.getSelection();
  if (!range) {
    quill.setSelection(quill.getLength(), 0);
  }

  const insertPosition = range ? range.index : quill.getLength();

  if (window.quillChallengeModal) {
    window.quillChallengeModal((challengeData) => {
      if (challengeData) {
        quill.insertEmbed(
          insertPosition,
          "challenge", // Utilisation de ChallengeBlot
          challengeData,
          Quill.sources.USER
        );
        quill.setSelection(insertPosition + 1, Quill.sources.SILENT);
      }
    });
  } else {
    console.error("La fonction quillChallengeModal n'est pas disponible");
  }
}

// Modules object for setting up the Quill editor
export const modules = {
  toolbar: {
    container: "#toolbar",
    handlers: {
      undo: undoChange,
      redo: redoChange,
      challenge: insertChallenge,
    },
  },
  history: {
    delay: 500,
    maxStack: 100,
    userOnly: true,
  },
};

// Formats objects for setting up the Quill editor
export const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "align",
  "strike",
  "script",
  "blockquote",
  "background",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "color",
  "code-block",
  "challenge",
];

export const QuillToolbar = ({ onSave, isSaving }) => {
  const { t } = useTranslation(); // Assigner useTranslation à {t}
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [challengeQuestion, setChallengeQuestion] = useState("");
  const [challengeOptions, setChallengeOptions] = useState(["", ""]);
  const [correctAnswers, setCorrectAnswers] = useState([]);
  const [callback, setCallback] = useState(null);

  // Fonction pour ouvrir la modale
  const openChallengeModal = (cb) => {
    if (typeof cb === "function") {
      setCallback(() => cb);
      setShowChallengeModal(true);
    } else {
      console.error("Callback isn't a function:", cb);
    }
  };

  // Exposer la fonction globalement pour que le handler puisse l'utiliser
  React.useEffect(() => {
    window.quillChallengeModal = openChallengeModal;

    return () => {
      // Nettoyage lors du démontage du composant
      window.quillChallengeModal = undefined;
    };
  }, []);

  // Ajouter la référence à la fonction openChallengeModal pour le handler
  if (typeof window !== "undefined" && window.Quill) {
    window.Quill.prototype.openChallengeModal = openChallengeModal;
  }

  // Gérer l'ajout d'une option
  const addOption = () => {
    setChallengeOptions([...challengeOptions, ""]);
  };

  // Gérer la suppression d'une option
  const removeOption = (index) => {
    const newOptions = [...challengeOptions];
    newOptions.splice(index, 1);
    setChallengeOptions(newOptions);

    // Mettre à jour les réponses correctes
    const newCorrectAnswers = correctAnswers
      .filter((i) => i !== index)
      .map((i) => (i > index ? i - 1 : i));
    setCorrectAnswers(newCorrectAnswers);
  };

  // Gérer le changement de texte d'une option
  const handleOptionChange = (index, value) => {
    const newOptions = [...challengeOptions];
    newOptions[index] = value;
    setChallengeOptions(newOptions);
  };

  // Gérer la sélection/désélection d'une réponse correcte
  const toggleCorrectAnswer = (index) => {
    if (correctAnswers.includes(index)) {
      setCorrectAnswers(correctAnswers.filter((i) => i !== index));
    } else {
      setCorrectAnswers([...correctAnswers, index]);
    }
  };

  // Gérer la soumission du formulaire
  const handleChallengeSubmit = () => {
    if (
      challengeQuestion.trim() === "" ||
      challengeOptions.some((opt) => opt.trim() === "")
    ) {
      alert(t("fill_all_fields"));
      return;
    }

    if (correctAnswers.length === 0) {
      alert(t("select_correct_answer"));
      return;
    }

    const challengeData = {
      question: challengeQuestion,
      options: challengeOptions,
      correct: correctAnswers,
    };

    // Assurons-nous que callback existe avant de l'appeler
    if (callback && typeof callback === "function") {
      callback(challengeData);
    } else {
      console.error("Callback is not a function or is undefined");
    }

    // Réinitialiser le formulaire
    setChallengeQuestion("");
    setChallengeOptions(["", ""]);
    setCorrectAnswers([]);
    setShowChallengeModal(false);
  };

  return (
    <>
      <div
        id="toolbar"
        className="d-flex justify-content-between align-items-center"
      >
        <div className="toolbar-controls d-flex flex-wrap">
          <span className="ql-formats">
            <select className="ql-font" defaultValue="arial">
              <option value="arial">Arial</option>
              <option value="comic-sans">Comic Sans</option>
              <option value="courier-new">Courier New</option>
              <option value="georgia">Georgia</option>
              <option value="helvetica">Helvetica</option>
              <option value="lucida">Lucida</option>
            </select>
            <select className="ql-size" defaultValue="medium">
              <option value="extra-small">{t("size_1")}</option>
              <option value="small">{t("size_2")}</option>
              <option value="medium">{t("size_3")}</option>
              <option value="large">{t("size_4")}</option>
            </select>
            <select className="ql-header" defaultValue="3">
              <option value="1">{t("heading")}</option>
              <option value="2">{t("subheading")}</option>
              <option value="3">{t("normal")}</option>
            </select>
          </span>
          <span className="ql-formats">
            <button className="ql-bold" />
            <button className="ql-italic" />
            <button className="ql-underline" />
            <button className="ql-strike" />
          </span>
          <span className="ql-formats">
            <button className="ql-list" value="ordered" />
            <button className="ql-list" value="bullet" />
            <button className="ql-indent" value="-1" />
            <button className="ql-indent" value="+1" />
          </span>
          <span className="ql-formats">
            <button className="ql-script" value="super" />
            <button className="ql-script" value="sub" />
            <button className="ql-blockquote" />
          </span>
          <span className="ql-formats">
            <select className="ql-align" />
            <select className="ql-color" />
            <select className="ql-background" />
          </span>
          <span className="ql-formats">
            <button className="ql-link" />
            <button className="ql-code-block" />
            <button className="ql-challenge">
              <FontAwesomeIcon icon={faLightbulb} />
            </button>
          </span>

          {onSave && (
            <div className="">
              <Button
                variant="outline-dark"
                onClick={onSave}
                disabled={isSaving}
                size="sm"
                className="save-button"
              >
                {isSaving ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                    <span className="ms-1">{t("saving")}...</span>
                  </>
                ) : (
                  t("save")
                )}
              </Button>
            </div>
          )}
          <span className="ql-formats" style={{ margin: "0px 5rem" }}>
            <button className="ql-undo">
              <CustomUndo />
            </button>
            <button className="ql-redo">
              <CustomRedo />
            </button>
          </span>
        </div>
      </div>

      {/* Modal pour créer un Challenge */}
      <Modal
        show={showChallengeModal}
        onHide={() => setShowChallengeModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("create_challenge")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t("question")}</Form.Label>
              <Form.Control
                type="text"
                placeholder={t("enter_question")}
                value={challengeQuestion}
                onChange={(e) => setChallengeQuestion(e.target.value)}
              />
            </Form.Group>

            <Form.Label>{t("options")}</Form.Label>
            {challengeOptions.map((option, index) => (
              <div key={index} className="d-flex mb-2 align-items-center">
                <Form.Check
                  type="checkbox"
                  label={t("correct")}
                  className="me-2"
                  checked={correctAnswers.includes(index)}
                  onChange={() => toggleCorrectAnswer(index)}
                />
                <Form.Control
                  type="text"
                  placeholder={`${t("option")} ${index + 1}`}
                  value={option}
                  onChange={(e) => handleOptionChange(index, e.target.value)}
                  className="me-2"
                />
                {challengeOptions.length > 2 && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => removeOption(index)}
                  >
                    {t("delete")}
                  </Button>
                )}
              </div>
            ))}

            <Button variant="secondary" className="mt-2" onClick={addOption}>
              {t("add_option")}
            </Button>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowChallengeModal(false)}
          >
            {t("cancel")}
          </Button>
          <Button variant="primary" onClick={handleChallengeSubmit}>
            {t("add_challenge")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default QuillToolbar;
